import { useEffect, useState } from 'react';

import type { AsyncState } from '../common';
import type { WorkItemDetails } from '../common/models';
import { getWorkItemDetails } from './api';

// Fetched once per session, so reopening a card shows its details at once.
const resolved = new Map<number, WorkItemDetails>();
const inFlight = new Map<number, Promise<WorkItemDetails>>();

const loadDetails = (id: number): Promise<WorkItemDetails> => {
    const pending = inFlight.get(id);
    if (pending) {
        return pending;
    }
    const request = getWorkItemDetails(id).then(
        (details) => {
            resolved.set(id, details);
            return details;
        },
        (error: unknown) => {
            // Forget the failure so the next open tries again.
            inFlight.delete(id);
            throw error;
        },
    );
    inFlight.set(id, request);
    return request;
};

const toError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));

const initialState = (id: number): AsyncState<WorkItemDetails> => {
    const known = resolved.get(id);
    return known ? { status: 'success', data: known } : { status: 'loading' };
};

export const useWorkItemDetails = (id: number): AsyncState<WorkItemDetails> => {
    const [state, setState] = useState(() => initialState(id));

    useEffect(() => {
        if (resolved.has(id)) {
            return;
        }
        let cancelled = false;
        loadDetails(id).then(
            (data) => {
                if (!cancelled) {
                    setState({ status: 'success', data });
                }
            },
            (e: unknown) => {
                if (!cancelled) {
                    setState({ status: 'error', error: toError(e) });
                }
            },
        );
        return () => {
            cancelled = true;
        };
    }, [id]);

    return state;
};
